import { useState, useEffect } from 'react';
import { Modal as BootstrapModal, Button, Alert, Badge, Card, Row, Col } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { BankAccountService } from '../../services/api/bankAccounts';
import { WithdrawalService } from '../../services/api/withdrawals';
import { balancesV2API } from '../../services/api/v2/withdrawals';
import { useConfirmation } from '../../hooks/useConfirmation';
import CurrencyFormatter from '../../utils/CurrencyFormatter';
import StatCard from '../common/StatCard';
import { Drawer } from '../../theme/components';

const WithdrawalsLayer = () => {
  const [showWithdrawalDrawer, setShowWithdrawalDrawer] = useState(false);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [balanceData, setBalanceData] = useState(null);
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);

  const [withdrawalForm, setWithdrawalForm] = useState({
    bank_account_id: '',
    user_notes: '',
    amount: ''
  });

  const [withdrawAllBalance, setWithdrawAllBalance] = useState(false);
  const [advanceSettings, setAdvanceSettings] = useState(null);
  const [anticipate, setAnticipate] = useState(false);

  const { modalProps, confirmAction } = useConfirmation();

  // Configurações
  const minWithdrawal = 1000; // R$ 10,00 mínimo (em centavos)

  // Carregar dados iniciais
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadBankAccounts(),
        loadBalance(),
        loadWithdrawalRequests(),
        loadAdvanceSettings()
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setError('Erro ao carregar dados da página.');
    } finally {
      setLoading(false);
    }
  };

  const loadBankAccounts = async () => {
    const response = await BankAccountService.getUserBankAccounts();
    setAccounts(response.data || []);
  };

  const loadBalance = async () => {
    const res = await balancesV2API.me();
    const b = res.data?.balance || {};
    const available = Number(b.available_cents) || 0;
    const pending = Number(b.pending_cents) || 0;
    // Mantém o shape esperado pelos componentes que já existem
    setBalanceData({
      available,
      pending,
      net_available: Math.max(0, available),
      total_withdrawn: 0,
    });
  };

  const loadWithdrawalRequests = async () => {
    const response = await WithdrawalService.getUserRequests();
    setWithdrawalRequests(response.data || []);
  };

  const loadAdvanceSettings = async () => {
    try {
      const response = await WithdrawalService.getAdvanceSettings();
      setAdvanceSettings(response.data);
    } catch (error) {
      console.error('Erro ao carregar configurações de antecipação:', error);
      setAdvanceSettings({ enabled: false });
    }
  };

  // Cálculos de antecipação
  const anticipatedGrossAmount = balanceData?.pending || 0;
  const anticipationFeePercentage = advanceSettings?.percentage_fee || 0;
  const anticipationFeeAmount = Math.round(anticipatedGrossAmount * (anticipationFeePercentage / 100));
  const anticipatedNetAmount = anticipatedGrossAmount - anticipationFeeAmount;

  const handleWithdrawalSubmit = async () => {
    try {
      setSubmitting(true);
      setError('');

      // Validações
      if (!withdrawalForm.bank_account_id) {
        setError('Selecione uma conta bancária para receber o saque.');
        return;
      }

      if (!withdrawalForm.amount) {
        setError('Digite o valor que deseja sacar.');
        return;
      }

      const amountInCents = parseInt(withdrawalForm.amount);

      if (amountInCents < minWithdrawal) {
        setError(`Valor mínimo para saque é ${formatCurrency(minWithdrawal)}`);
        return;
      }

      if (amountInCents > balanceData.net_available) {
        setError(`Valor solicitado excede o saldo disponível: ${formatCurrency(balanceData.net_available)}`);
        return;
      }

      const requestData = {
        amount: amountInCents,
        bank_account_id: parseInt(withdrawalForm.bank_account_id),
        user_notes: withdrawalForm.user_notes
      };

      // Adicionar dados de antecipação se ativada
      if (anticipate && advanceSettings?.enabled && anticipatedGrossAmount > 0) {
        requestData.anticipate = true;
        requestData.anticipated_amount = anticipatedGrossAmount;
        requestData.anticipation_fee = anticipationFeeAmount;
        requestData.anticipation_plan_code = advanceSettings.plan_code || null;
      }

      await WithdrawalService.createRequest(requestData);

      setSuccess('Solicitação de saque enviada com sucesso!');
      setShowWithdrawalDrawer(false);
      resetWithdrawalForm();

      await loadBalance();
      await loadWithdrawalRequests();

      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Erro ao solicitar saque:', error);
      setError(error.response?.data?.message || 'Erro ao solicitar saque. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetWithdrawalForm = () => {
    setWithdrawalForm({ bank_account_id: '', user_notes: '', amount: '' });
    setWithdrawAllBalance(false);
    setAnticipate(false);
    setError('');
  };

  const handleCloseWithdrawalDrawer = () => {
    setShowWithdrawalDrawer(false);
    resetWithdrawalForm();
  };

  const formatCurrency = (valueInCents) => {
    return CurrencyFormatter.centsToFormatted(valueInCents);
  };

  const handleAmountChange = (e) => {
    const input = e.target.value;
    const numbersOnly = input.replace(/\D/g, '');
    setWithdrawalForm({ ...withdrawalForm, amount: numbersOnly });
  };

  const getFormattedAmountInput = () => {
    if (!withdrawalForm.amount) return '';
    return formatCurrency(parseInt(withdrawalForm.amount));
  };

  const handleWithdrawAllToggle = () => {
    const newValue = !withdrawAllBalance;
    setWithdrawAllBalance(newValue);

    if (newValue && balanceData) {
      setWithdrawalForm({ ...withdrawalForm, amount: balanceData.net_available.toString() });
    } else if (!newValue) {
      setWithdrawalForm({ ...withdrawalForm, amount: '' });
    }
  };

  const formatStatus = (status) => {
    const statusMap = {
      pending: { text: 'Em análise', variant: 'warning' },
      approved: { text: 'Aprovado', variant: 'info' },
      rejected: { text: 'Rejeitado', variant: 'danger' },
      processed: { text: 'Processado', variant: 'success' },
      cancelled: { text: 'Cancelado', variant: 'secondary' }
    };
    return statusMap[status] || { text: status, variant: 'secondary' };
  };

  const canWithdraw = balanceData && balanceData.net_available >= minWithdrawal && accounts.length > 0;

  // Calcular o total que o usuário receberá
  const getWithdrawalAmount = () => {
    const amount = withdrawalForm.amount ? parseInt(withdrawalForm.amount) : 0;
    if (anticipate && advanceSettings?.enabled) {
      return amount + anticipatedNetAmount;
    }
    return amount;
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
        <p className="mt-3 text-muted">Carregando dados de saque...</p>
      </div>
    );
  }

  return (
    <>
      <div className="card bg-transparent border-0">
        {/* Disclaimer de titularidade */}
        <div className="card-body border-bottom">
          <Alert variant="warning" className="mb-0">
            <div className="d-flex align-items-start gap-2">
              <Icon icon="mdi:alert-circle-outline" className="mt-1" />
              <div>
                <strong>Segurança:</strong> Os saques só podem ser processados para contas bancárias
                cadastradas em seu nome. Verifique se todas as informações estão corretas antes de solicitar.
              </div>
            </div>
          </Alert>
        </div>

        <div className="card-body">
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          {/* Cards de resumo */}
          <Row className="g-3" style={{ marginBottom: '24px' }}>
            <Col md={3}>
              <StatCard
                title="Disponível para Saque"
                value={balanceData ? formatCurrency(balanceData.net_available) : 'R$ 0,00'}
                subtitle={canWithdraw ? 'Solicitar Saque' : 'Saldo insuficiente'}
                icon="mdi:cash-multiple"
                variant="green"
                onClickValue={canWithdraw ? () => setShowWithdrawalDrawer(true) : undefined}
                onClickIcon={canWithdraw ? () => setShowWithdrawalDrawer(true) : undefined}
              />
            </Col>

            <Col md={3}>
              <StatCard
                title="Saldo em Análise"
                value={balanceData ? formatCurrency(balanceData.pending) : 'R$ 0,00'}
                subtitle="Aguardando liberação (D+)"
                icon="mdi:clock-time-four-outline"
                variant="orange"
              />
            </Col>

            <Col md={3}>
              <StatCard
                title="Saldo Total"
                value={balanceData ? formatCurrency((balanceData.net_available || 0) + (balanceData.pending || 0)) : 'R$ 0,00'}
                subtitle="Disponível + Em análise"
                icon="mdi:wallet"
                variant="blue"
              />
            </Col>

            <Col md={3}>
              <StatCard
                title="Total já Sacado"
                value={balanceData ? formatCurrency(balanceData.paid || 0) : 'R$ 0,00'}
                subtitle="Histórico de saques"
                icon="mdi:cash-check"
                variant="purple"
              />
            </Col>
          </Row>

          {/* Botão Solicitar Saque */}
          <div style={{ marginBottom: '24px' }}>
            <button
              onClick={() => setShowWithdrawalDrawer(true)}
              disabled={!canWithdraw}
              className="btn btn-success fw-semibold"
              style={{ fontSize: '14px', padding: '10px 24px', borderRadius: '8px' }}
            >
              <Icon icon="mdi:cash" className="me-2" style={{ fontSize: '18px' }} />
              {canWithdraw ? 'Solicitar Saque' : 'Saldo insuficiente para saque'}
            </button>
          </div>

          {/* Tabela de saques */}
          <Card>
            <Card.Header>
              <span className="mb-0 d-flex align-items-center gap-2 fw-semibold" style={{ fontSize: '16px' }}>
                <Icon icon="mdi:history" /> Histórico de Saques
              </span>
            </Card.Header>
            <Card.Body>
              <div className="table-responsive">
                <table className="table colored-row-table text-sm">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Data da solicitação</th>
                      <th>Conta bancária</th>
                      <th>Valor</th>
                      <th>Status</th>
                      <th>Observação</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawalRequests.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-4 text-secondary-light">
                          Nenhum saque realizado ainda
                        </td>
                      </tr>
                    ) : (
                      withdrawalRequests.map((request) => {
                        const statusInfo = formatStatus(request.status);
                        return (
                          <tr key={request.id}>
                            <td>#{request.id}</td>
                            <td>{new Date(request.created_at).toLocaleDateString('pt-BR')}</td>
                            <td>
                              {request.bank_account ?
                                `${request.bank_account.bank_name} - ${request.bank_account.account_type}` :
                                'N/A'
                              }
                            </td>
                            <td>
                              {formatCurrency(request.amount)}
                              {request.has_anticipation && (
                                <Badge bg="info" className="ms-1" style={{ fontSize: '9px' }}>Antecipação</Badge>
                              )}
                            </td>
                            <td>
                              <Badge bg={statusInfo.variant}>
                                {statusInfo.text}
                              </Badge>
                            </td>
                            <td className="text-truncate" style={{ maxWidth: '200px' }}>
                              {request.user_notes || request.admin_notes || request.rejection_reason || '-'}
                            </td>
                            <td>
                              <button
                                onClick={() => {
                                  setSelectedWithdrawal(request);
                                  setShowDetailsDrawer(true);
                                }}
                                className="btn btn-sm btn-outline-primary"
                                style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', padding: '4px 12px' }}
                              >
                                <Icon icon="mdi:eye" size={14} />
                                Ver Detalhes
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Drawer de Detalhes da Solicitação */}
      <Drawer
        show={showDetailsDrawer}
        onHide={() => setShowDetailsDrawer(false)}
        title={`Solicitação #${selectedWithdrawal?.id || ''}`}
        icon="mdi:cash-multiple"
        size="md"
        footer={
          <Drawer.Button
            variant="secondary"
            onClick={() => setShowDetailsDrawer(false)}
          >
            Fechar
          </Drawer.Button>
        }
      >
        {selectedWithdrawal && (
          <>
            {/* Status */}
            <Drawer.Section title="Status" icon="mdi:information">
              <Drawer.InfoCard
                variant={
                  selectedWithdrawal.status === 'paid' || selectedWithdrawal.status === 'processed' ? 'success' :
                  selectedWithdrawal.status === 'pending' ? 'info' :
                  selectedWithdrawal.status === 'rejected' ? 'danger' :
                  selectedWithdrawal.status === 'cancelled' ? 'warning' :
                  'neutral'
                }
              >
                <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                  <Icon
                    icon={
                      selectedWithdrawal.status === 'paid' || selectedWithdrawal.status === 'processed' ? 'mdi:check-circle' :
                      selectedWithdrawal.status === 'pending' ? 'mdi:clock-outline' :
                      selectedWithdrawal.status === 'rejected' ? 'mdi:close-circle' :
                      selectedWithdrawal.status === 'cancelled' ? 'mdi:cancel' :
                      'mdi:help-circle'
                    }
                    style={{ fontSize: '24px', flexShrink: 0, marginTop: '2px' }}
                  />
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: '16px', display: 'block', marginBottom: '8px' }}>
                      {formatStatus(selectedWithdrawal.status).text}
                    </strong>
                    <p style={{ margin: 0, fontSize: '13px', opacity: 0.9 }}>
                      {selectedWithdrawal.status === 'paid' && 'Seu saque foi processado com sucesso'}
                      {selectedWithdrawal.status === 'processed' && 'Seu saque foi processado com sucesso'}
                      {selectedWithdrawal.status === 'pending' && 'Sua solicitação será processada em até 2 dias úteis'}
                      {selectedWithdrawal.status === 'approved' && 'Sua solicitação foi aprovada e será processada em breve'}
                      {selectedWithdrawal.status === 'rejected' && 'Sua solicitação foi rejeitada'}
                      {selectedWithdrawal.status === 'cancelled' && 'Esta solicitação foi cancelada'}
                    </p>
                  </div>
                </div>
              </Drawer.InfoCard>
            </Drawer.Section>

            <Drawer.Divider />

            {/* Valores */}
            <Drawer.Section title="Informações Financeiras" icon="mdi:cash">
              <Drawer.Row label="Valor Bruto" value={formatCurrency(selectedWithdrawal.amount)} />
              {selectedWithdrawal.has_anticipation && (
                <>
                  <Drawer.Row label="Valor Antecipado" value={formatCurrency(selectedWithdrawal.anticipated_amount)} />
                  <Drawer.Row label="Taxa de Antecipação" value={`- ${formatCurrency(selectedWithdrawal.anticipation_fee)}`} />
                  {selectedWithdrawal.anticipation_plan_code && (
                    <Drawer.Row label="Plano" value={selectedWithdrawal.anticipation_plan_code} />
                  )}
                </>
              )}
              <Drawer.Row label="Valor Líquido" value={formatCurrency(selectedWithdrawal.net_amount || selectedWithdrawal.amount)} highlight />
            </Drawer.Section>

            <Drawer.Divider />

            {/* Conta Bancária */}
            <Drawer.Section title="Dados Bancários" icon="mdi:bank">
              {selectedWithdrawal.bank_account ? (
                <>
                  <Drawer.Row label="Banco" value={selectedWithdrawal.bank_account.bank_name} />
                  <Drawer.Row label="Agência" value={selectedWithdrawal.bank_account.agency} />
                  <Drawer.Row
                    label="Conta"
                    value={`${selectedWithdrawal.bank_account.account_number}${selectedWithdrawal.bank_account.account_digit ? `-${selectedWithdrawal.bank_account.account_digit}` : ''}`}
                  />
                  <Drawer.Row
                    label="Tipo"
                    value={selectedWithdrawal.bank_account.account_type === 'corrente' ? 'Conta Corrente' : 'Conta Poupança'}
                  />
                  <Drawer.Row label="Titular" value={selectedWithdrawal.bank_account.account_holder_name} />
                </>
              ) : (
                <Drawer.EmptyState icon="mdi:bank-off" text="Informações bancárias não disponíveis" />
              )}
            </Drawer.Section>

            <Drawer.Divider />

            {/* Observações */}
            <Drawer.Section title="Observações" icon="mdi:note-text">
              {selectedWithdrawal.user_notes && (
                <Drawer.InfoCard variant="neutral">
                  <div>
                    <strong style={{ fontSize: '13px', display: 'block', marginBottom: '4px' }}>Sua observação:</strong>
                    <p style={{ margin: 0, fontSize: '14px' }}>{selectedWithdrawal.user_notes}</p>
                  </div>
                </Drawer.InfoCard>
              )}
              {selectedWithdrawal.admin_notes && (
                <Drawer.InfoCard variant="info">
                  <div>
                    <strong style={{ fontSize: '13px', display: 'block', marginBottom: '4px' }}>Observação do administrador:</strong>
                    <p style={{ margin: 0, fontSize: '14px' }}>{selectedWithdrawal.admin_notes}</p>
                  </div>
                </Drawer.InfoCard>
              )}
              {selectedWithdrawal.rejection_reason && (
                <Drawer.InfoCard variant="danger">
                  <div>
                    <strong style={{ fontSize: '13px', display: 'block', marginBottom: '4px' }}>Motivo da rejeição:</strong>
                    <p style={{ margin: 0, fontSize: '14px' }}>{selectedWithdrawal.rejection_reason}</p>
                  </div>
                </Drawer.InfoCard>
              )}
              {!selectedWithdrawal.user_notes && !selectedWithdrawal.admin_notes && !selectedWithdrawal.rejection_reason && (
                <Drawer.EmptyState icon="mdi:note-off" text="Sem observações" />
              )}
            </Drawer.Section>

            <Drawer.Divider />

            {/* Histórico */}
            <Drawer.Section title="Histórico" icon="mdi:history">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', gap: '12px', paddingLeft: '8px', borderLeft: '2px solid #10B981' }}>
                  <Icon icon="mdi:check-circle" style={{ color: '#10B981', fontSize: '20px', marginTop: '2px', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 600, color: '#111827' }}>Solicitação Criada</p>
                    <p style={{ margin: 0, fontSize: '13px', color: '#6B7280' }}>
                      {new Date(selectedWithdrawal.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>

                {selectedWithdrawal.updated_at && selectedWithdrawal.updated_at !== selectedWithdrawal.created_at && (
                  <div style={{
                    display: 'flex', gap: '12px', paddingLeft: '8px',
                    borderLeft: `2px solid ${selectedWithdrawal.status === 'paid' || selectedWithdrawal.status === 'processed' ? '#10B981' : selectedWithdrawal.status === 'rejected' ? '#EF4444' : selectedWithdrawal.status === 'cancelled' ? '#F59E0B' : '#6B7280'}`
                  }}>
                    <Icon
                      icon={selectedWithdrawal.status === 'paid' || selectedWithdrawal.status === 'processed' ? 'mdi:check-circle' : selectedWithdrawal.status === 'rejected' ? 'mdi:close-circle' : selectedWithdrawal.status === 'cancelled' ? 'mdi:cancel' : 'mdi:clock-outline'}
                      style={{
                        color: selectedWithdrawal.status === 'paid' || selectedWithdrawal.status === 'processed' ? '#10B981' : selectedWithdrawal.status === 'rejected' ? '#EF4444' : selectedWithdrawal.status === 'cancelled' ? '#F59E0B' : '#6B7280',
                        fontSize: '20px', marginTop: '2px', flexShrink: 0
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 600, color: '#111827' }}>{formatStatus(selectedWithdrawal.status).text}</p>
                      <p style={{ margin: 0, fontSize: '13px', color: '#6B7280' }}>
                        {new Date(selectedWithdrawal.updated_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                )}

                {selectedWithdrawal.status === 'pending' && (
                  <div style={{ display: 'flex', gap: '12px', paddingLeft: '8px', borderLeft: '2px solid #D1D5DB', opacity: 0.6 }}>
                    <Icon icon="mdi:clock-outline" style={{ color: '#6B7280', fontSize: '20px', marginTop: '2px', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 600, color: '#111827' }}>Processamento</p>
                      <p style={{ margin: 0, fontSize: '13px', color: '#6B7280' }}>Previsão: até 2 dias úteis</p>
                    </div>
                  </div>
                )}
              </div>
            </Drawer.Section>
          </>
        )}
      </Drawer>

      {/* ======= DRAWER DE SOLICITAR SAQUE ======= */}
      <Drawer
        show={showWithdrawalDrawer}
        onHide={handleCloseWithdrawalDrawer}
        title="Solicitar Saque"
        icon="mdi:cash"
        size="lg"
        footer={
          <div className="d-flex gap-2 w-100">
            <Drawer.Button variant="secondary" onClick={handleCloseWithdrawalDrawer}>
              Cancelar
            </Drawer.Button>
            <Drawer.Button
              variant="success"
              onClick={handleWithdrawalSubmit}
              loading={submitting}
              disabled={!withdrawalForm.bank_account_id || !withdrawalForm.amount || !canWithdraw}
              icon="mdi:cash-check"
            >
              Confirmar Solicitação
            </Drawer.Button>
          </div>
        }
      >
        {/* Seção: Saldo */}
        <Drawer.Section title="Seu Saldo" icon="mdi:wallet">
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '16px', background: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)',
            border: '1px solid #BBF7D0', borderRadius: '10px', marginBottom: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Icon icon="mdi:cash-multiple" style={{ fontSize: '22px', color: '#16A34A' }} />
              <span style={{ fontWeight: 500, fontSize: '14px', color: '#166534' }}>Disponível para saque</span>
            </div>
            <span style={{ fontSize: '20px', fontWeight: 700, color: '#166534' }}>
              {balanceData ? formatCurrency(balanceData.net_available) : 'R$ 0,00'}
            </span>
          </div>

          {balanceData?.pending > 0 && (
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 16px', background: '#FFFBEB', border: '1px solid #FDE68A',
              borderRadius: '10px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Icon icon="mdi:clock-time-four-outline" style={{ fontSize: '18px', color: '#D97706' }} />
                <span style={{ fontWeight: 500, fontSize: '13px', color: '#92400E' }}>Saldo pendente (em carência)</span>
              </div>
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#92400E' }}>
                {formatCurrency(balanceData.pending)}
              </span>
            </div>
          )}
        </Drawer.Section>

        <Drawer.Divider />

        {/* Seção: Antecipação de Recebíveis */}
        {advanceSettings?.enabled && anticipatedGrossAmount > 0 && (
          <>
            <Drawer.Section title="Antecipação de Recebíveis" icon="mdi:lightning-bolt">
              <Drawer.InfoCard variant="info">
                <div>
                  <span style={{ display: 'block', fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
                    Você possui {formatCurrency(anticipatedGrossAmount)} em saldo pendente
                  </span>
                  <span style={{ display: 'block', fontSize: '13px', color: '#6B7280' }}>
                    Antecipe seus recebíveis com uma taxa de {anticipationFeePercentage}%
                    {advanceSettings.plan_name ? ` (Plano: ${advanceSettings.plan_name})` : ''}
                  </span>
                </div>
              </Drawer.InfoCard>

              {/* Toggle de antecipação */}
              <div
                onClick={() => setAnticipate(!anticipate)}
                style={{
                  padding: '16px',
                  background: anticipate ? 'linear-gradient(135deg, #EBF4FF 0%, #E0EAFF 100%)' : 'linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)',
                  border: anticipate ? '2px solid #6366F1' : '1px solid #E5E7EB',
                  borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: 'pointer', transition: 'all 0.3s ease',
                  boxShadow: anticipate ? '0 4px 12px rgba(99, 102, 241, 0.15)' : 'none',
                  marginTop: '12px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Icon icon="mdi:lightning-bolt" style={{ fontSize: '22px', color: anticipate ? '#6366F1' : '#6B7280' }} />
                  <div>
                    <span style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: anticipate ? '#4F46E5' : '#111827', marginBottom: '2px' }}>
                      Antecipar recebíveis
                    </span>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>
                      Receba {formatCurrency(anticipatedNetAmount)} (líquido após taxa)
                    </span>
                  </div>
                </div>
                <div style={{
                  width: '48px', height: '28px', background: anticipate ? '#6366F1' : '#E5E7EB',
                  borderRadius: '14px', position: 'relative', transition: 'all 0.3s ease'
                }}>
                  <div style={{
                    width: '22px', height: '22px', background: '#FFFFFF', borderRadius: '50%',
                    position: 'absolute', top: '3px', left: anticipate ? '23px' : '3px',
                    transition: 'all 0.3s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }} />
                </div>
              </div>

              {/* Detalhes da antecipação quando ativada */}
              {anticipate && (
                <div style={{ marginTop: '12px', padding: '14px', background: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '13px', color: '#6B7280' }}>Valor bruto pendente</span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>{formatCurrency(anticipatedGrossAmount)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '13px', color: '#6B7280' }}>Taxa de antecipação ({anticipationFeePercentage}%)</span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#EF4444' }}>- {formatCurrency(anticipationFeeAmount)}</span>
                  </div>
                  <div style={{ height: '1px', background: '#E2E8F0', margin: '8px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>Valor líquido antecipado</span>
                    <span style={{ fontSize: '14px', fontWeight: 700, color: '#10B981' }}>{formatCurrency(anticipatedNetAmount)}</span>
                  </div>
                </div>
              )}
            </Drawer.Section>

            <Drawer.Divider />
          </>
        )}

        {/* Seção: Valor do Saque */}
        <Drawer.Section title="Valor do Saque" icon="mdi:cash">
          {/* Toggle sacar tudo */}
          <div
            onClick={handleWithdrawAllToggle}
            style={{
              padding: '14px 16px',
              background: withdrawAllBalance ? 'linear-gradient(135deg, #EBF4FF 0%, #E0EAFF 100%)' : 'linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)',
              border: withdrawAllBalance ? '2px solid #6366F1' : '1px solid #E5E7EB',
              borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              cursor: 'pointer', transition: 'all 0.3s ease',
              boxShadow: withdrawAllBalance ? '0 4px 12px rgba(99, 102, 241, 0.15)' : 'none',
              marginBottom: '12px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Icon icon="mdi:cash-multiple" style={{ fontSize: '20px', color: withdrawAllBalance ? '#6366F1' : '#6B7280' }} />
              <div>
                <span style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: withdrawAllBalance ? '#4F46E5' : '#111827' }}>
                  Sacar todo o saldo disponível
                </span>
                <span style={{ fontSize: '12px', color: '#6B7280' }}>
                  {balanceData ? formatCurrency(balanceData.net_available) : 'R$ 0,00'}
                </span>
              </div>
            </div>
            <div style={{
              width: '48px', height: '28px', background: withdrawAllBalance ? '#6366F1' : '#E5E7EB',
              borderRadius: '14px', position: 'relative', transition: 'all 0.3s ease'
            }}>
              <div style={{
                width: '22px', height: '22px', background: '#FFFFFF', borderRadius: '50%',
                position: 'absolute', top: '3px', left: withdrawAllBalance ? '23px' : '3px',
                transition: 'all 0.3s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }} />
            </div>
          </div>

          {/* Input de valor */}
          <input
            type="text"
            value={getFormattedAmountInput()}
            onChange={handleAmountChange}
            placeholder="R$ 0,00"
            disabled={withdrawAllBalance}
            style={{
              width: '100%', fontSize: '24px', fontWeight: 700, padding: '16px',
              border: withdrawAllBalance ? '2px solid #E5E7EB' : '2px solid #D1D5DB',
              borderRadius: '10px', transition: 'all 0.2s',
              opacity: withdrawAllBalance ? 0.5 : 1,
              cursor: withdrawAllBalance ? 'not-allowed' : 'text',
              background: withdrawAllBalance ? '#F3F4F6' : '#FFFFFF',
              outline: 'none', color: '#111827'
            }}
            onFocus={(e) => { if (!withdrawAllBalance) { e.target.style.borderColor = '#6366F1'; e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)'; } }}
            onBlur={(e) => { e.target.style.borderColor = '#D1D5DB'; e.target.style.boxShadow = 'none'; }}
          />
          <small style={{ color: '#6B7280', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '8px', fontSize: '12px' }}>
            <Icon icon="mdi:information-outline" style={{ fontSize: '15px' }} />
            Mínimo: {formatCurrency(minWithdrawal)} | Máximo: {balanceData ? formatCurrency(balanceData.net_available) : 'R$ 0,00'}
          </small>
        </Drawer.Section>

        <Drawer.Divider />

        {/* Seção: Conta Bancária */}
        <Drawer.Section title="Conta Bancária" icon="mdi:bank">
          <select
            value={withdrawalForm.bank_account_id}
            onChange={(e) => setWithdrawalForm({ ...withdrawalForm, bank_account_id: e.target.value })}
            style={{
              width: '100%', padding: '12px 16px', border: '2px solid #D1D5DB',
              borderRadius: '10px', fontSize: '14px', outline: 'none',
              background: '#FFFFFF', cursor: 'pointer', color: '#111827', fontWeight: 500
            }}
            onFocus={(e) => { e.target.style.borderColor = '#6366F1'; e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)'; }}
            onBlur={(e) => { e.target.style.borderColor = '#D1D5DB'; e.target.style.boxShadow = 'none'; }}
          >
            <option value="">Selecione a conta para receber</option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.bank_name} - {account.account_type === 'corrente' ? 'Corrente' : 'Poupança'} -
                Ag: {account.agency} | Conta: {account.account_number}{account.account_digit ? `-${account.account_digit}` : ''}
              </option>
            ))}
          </select>
          {accounts.length === 0 && (
            <small style={{ color: '#E63737', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '10px', fontSize: '12px' }}>
              <Icon icon="mdi:alert-circle" style={{ fontSize: '15px' }} />
              Você precisa cadastrar uma conta bancária antes de solicitar saque
            </small>
          )}
        </Drawer.Section>

        <Drawer.Divider />

        {/* Seção: Observações */}
        <Drawer.Section title="Observações (opcional)" icon="mdi:note-text">
          <textarea
            value={withdrawalForm.user_notes}
            onChange={(e) => setWithdrawalForm({ ...withdrawalForm, user_notes: e.target.value })}
            placeholder="Alguma informação adicional sobre este saque..."
            rows={3}
            style={{
              width: '100%', padding: '12px 16px', border: '2px solid #D1D5DB',
              borderRadius: '10px', fontSize: '14px', resize: 'vertical',
              outline: 'none', background: '#FFFFFF', color: '#111827',
              fontFamily: 'inherit', lineHeight: '1.5'
            }}
            onFocus={(e) => { e.target.style.borderColor = '#6366F1'; e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)'; }}
            onBlur={(e) => { e.target.style.borderColor = '#D1D5DB'; e.target.style.boxShadow = 'none'; }}
          />
        </Drawer.Section>

        <Drawer.Divider />

        {/* Seção: Resumo */}
        <Drawer.Section title="Resumo da Solicitação" icon="mdi:receipt-text">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {/* Valor do saldo disponível */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#6B7280', fontWeight: 500 }}>Valor a sacar (disponível)</span>
              <span style={{ fontSize: '15px', color: '#111827', fontWeight: 700 }}>
                {withdrawalForm.amount ? formatCurrency(parseInt(withdrawalForm.amount)) : 'R$ 0,00'}
              </span>
            </div>

            {/* Valores de antecipação */}
            {anticipate && advanceSettings?.enabled && anticipatedGrossAmount > 0 && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', color: '#6B7280', fontWeight: 500 }}>Valor antecipado (bruto)</span>
                  <span style={{ fontSize: '15px', color: '#111827', fontWeight: 600 }}>
                    + {formatCurrency(anticipatedGrossAmount)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', color: '#6B7280', fontWeight: 500 }}>Taxa de antecipação ({anticipationFeePercentage}%)</span>
                  <span style={{ fontSize: '15px', color: '#EF4444', fontWeight: 600 }}>
                    - {formatCurrency(anticipationFeeAmount)}
                  </span>
                </div>
              </>
            )}

            {/* Saldo restante */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#6B7280', fontWeight: 500 }}>Saldo restante</span>
              <span style={{ fontSize: '15px', color: '#F59E0B', fontWeight: 700 }}>
                {withdrawalForm.amount && balanceData ?
                  formatCurrency(Math.max(0, balanceData.net_available - parseInt(withdrawalForm.amount))) :
                  formatCurrency(balanceData?.net_available || 0)
                }
              </span>
            </div>

            <div style={{ height: '1px', background: '#E5E7EB', margin: '4px 0' }} />

            {/* Total a receber */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '18px', background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              borderRadius: '10px', marginTop: '4px', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)'
            }}>
              <span style={{ fontSize: '15px', fontWeight: 600, color: '#FFFFFF' }}>Você receberá</span>
              <span style={{ fontSize: '22px', fontWeight: 700, color: '#FFFFFF' }}>
                {formatCurrency(getWithdrawalAmount())}
              </span>
            </div>

            {/* Info de processamento */}
            <div style={{
              marginTop: '8px', padding: '10px 12px', background: '#F9FAFB',
              borderRadius: '6px', borderLeft: '3px solid #6366F1'
            }}>
              <p style={{ margin: 0, fontSize: '12px', color: '#6B7280', lineHeight: '1.5' }}>
                <strong style={{ color: '#111827' }}>Processamento:</strong> até 2 dias úteis após aprovação
              </p>
            </div>
          </div>
        </Drawer.Section>

        {/* Erro dentro do drawer */}
        {error && (
          <div style={{ padding: '0 24px 16px' }}>
            <Drawer.InfoCard variant="danger">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon icon="mdi:alert-circle" style={{ fontSize: '20px' }} />
                {error}
              </div>
            </Drawer.InfoCard>
          </div>
        )}
      </Drawer>

      {/* Modal de confirmação */}
      <BootstrapModal {...modalProps} />
    </>
  );
};

export default WithdrawalsLayer;
